import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ResourceType } from "@common/types/auditLog";
import { FinancialAccount } from "@common/types/financialAccount";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { DocumentName } from "@components/documents/DocumentName";
import { HistoryTransactionDetailsRow } from "../transactions/views/historyTransactionDetailsViewConfig";
import { getStringField } from "../../utils/resourceFields";
import styles from "./HistoryDetailsCells.module.scss";

const getResourceLabel = (row: HistoryTransactionDetailsRow): string => {
    const { resource, resourceType, resourceId, field, oldValue, newValue } =
        row;

    const nameFromDiff =
        field === "name"
            ? ((newValue.value as string | null) ??
              (oldValue.value as string | null))
            : null;

    if (!resource) {
        if (resourceType === ResourceType.TRANSACTION_RULE_GROUP) {
            return nameFromDiff ?? "Deleted rule group";
        }
        return "Deleted Transaction";
    }

    switch (resourceType) {
        case ResourceType.TRANSACTION:
            return (
                (resource.bankDescription as string) ??
                (resource.description as string) ??
                resourceId
            );
        case ResourceType.TASK:
            return (resource.title as string) ?? resourceId;
        case ResourceType.ENTITY:
            return (resource.name as string) ?? resourceId;
        case ResourceType.CATEGORY:
            return (resource.name as string) ?? resourceId;
        case ResourceType.COUNTERPARTY:
            return (resource.name as string) ?? resourceId;
        case ResourceType.FINANCIAL_ACCOUNT:
            return (
                (resource.name as string) ??
                (resource.accountName as string) ??
                resourceId
            );
        case ResourceType.JOURNAL_ENTRY:
            return (resource.memo as string) ?? `Journal Entry #${resourceId}`;
        case ResourceType.USER:
            return (
                (resource.name as string) ??
                (resource.email as string) ??
                resourceId
            );
        case ResourceType.BANK_RECONCILIATION: {
            const financialAccount =
                resource.financialAccount as FinancialAccount;
            return `Reconciliation ${financialAccount.name ?? ""}`.trim();
        }
        case ResourceType.TRANSACTION_FIELD_REVIEW:
            return "Transaction review";
        case ResourceType.FINANCIAL_DOCUMENT:
            return (resource.fileName as string) ?? resourceId;
        case ResourceType.ACCOUNT:
            return (resource.name as string) ?? resourceId;
        case ResourceType.TRANSACTION_RULE_GROUP: {
            return (resource.name as string) ?? nameFromDiff ?? "Rule group";
        }
        case ResourceType.BILL: {
            const number = getStringField(resource, "invoiceNumber");
            const description = getStringField(resource, "description");
            if (number) {
                return `Bill #${number}`;
            }
            if (description) {
                return description;
            }
            return `Bill ${resourceId.slice(0, 8)}`;
        }
        case ResourceType.INVOICE: {
            const number = getStringField(resource, "invoiceNumber");
            const description = getStringField(resource, "description");
            if (number) {
                return `Invoice #${number}`;
            }
            if (description) {
                return description;
            }
            return `Invoice ${resourceId.slice(0, 8)}`;
        }
        default:
            return resourceId;
    }
};

export const HistoryDetailsDescriptionCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        HistoryTransactionDetailsRow,
        string
    >) => {
        const label = getResourceLabel(row.original);
        const { subDescription, resourceType, resourceId, resource } =
            row.original;

        const isDocument =
            resourceType === ResourceType.FINANCIAL_DOCUMENT && !!resource;
        const description = isDocument ? (
            <DocumentName
                fileName={label}
                icon
                documentId={resourceId}
                className={styles.description}
            />
        ) : (
            <BreakableText
                className={styles.description}
                text={label}
                showTooltip
            />
        );

        if (!subDescription) {
            return description;
        }

        return (
            <div className={styles.descriptionContainer}>
                {description}
                <BreakableText
                    className={styles.subDescription}
                    text={subDescription}
                    showTooltip
                />
            </div>
        );
    },
);

HistoryDetailsDescriptionCell.displayName = "HistoryDetailsDescriptionCell";
